import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { formatDisplayName } from 'common/utils'
import type {
  GetPylonChatWidgetIdentity,
  GetPylonChatWidgetIdentityVariables,
} from 'types/graphql'

import { TypedDocumentNode, useQuery } from '@cedarjs/web'

import { useEmrAuth } from 'src/auth'
import { createNamedContext } from 'src/utils'

const PYLON_APP_ID = 'd29ce092-22ec-42c6-be23-e0778c6c4ba7'
const PYLON_WIDGET_SCRIPT_URL = `https://widget.usepylon.com/widget/${PYLON_APP_ID}`
const PYLON_SCRIPT_DATA_ATTR = 'data-pylon-widget'

const HELP_NAV_ITEM_SELECTOR = 'button[data-testid="Help"]'
const PANEL_GAP_PX = 8

const ensurePylonScriptInjected = () => {
  // Stand up the call-queue stub before the real widget loads, matching
  // Pylon's official loader — calls made before the script finishes loading
  // are replayed once it does.
  if (!window.Pylon) {
    const queue: unknown[][] = []
    const stub = ((...args: unknown[]) => {
      queue.push(args)
    }) as Window['Pylon'] & { q: unknown[][] }
    stub.q = queue
    window.Pylon = stub
  }

  if (document.querySelector(`script[${PYLON_SCRIPT_DATA_ATTR}]`)) return

  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.async = true
  script.src = PYLON_WIDGET_SCRIPT_URL
  script.setAttribute(PYLON_SCRIPT_DATA_ATTR, 'true')
  document.head.appendChild(script)
}

const PYLON_CHAT_WIDGET_IDENTITY_QUERY: TypedDocumentNode<
  GetPylonChatWidgetIdentity,
  GetPylonChatWidgetIdentityVariables
> = gql`
  query GetPylonChatWidgetIdentity {
    pylonChatWidgetIdentity {
      email
      emailHash
    }
  }
`

interface PylonChatWidgetContextValue {
  isEnabled: boolean
  // True once chat_settings have been attached to window.pylon — guards the
  // small race where Help renders before the identity query resolves.
  isReady: boolean
  isOpen: boolean
  unreadCount: number
  show: () => void
  hide: () => void
  toggle: () => void
}

const NOOP = () => {}

const DISABLED_VALUE: PylonChatWidgetContextValue = {
  isEnabled: false,
  isReady: false,
  isOpen: false,
  unreadCount: 0,
  show: NOOP,
  hide: NOOP,
  toggle: NOOP,
}

const PylonChatWidgetContext = createNamedContext<PylonChatWidgetContextValue>(
  'PylonChatWidgetContext',
  DISABLED_VALUE
)

export const usePylonChatWidget = () => {
  const context = useContext(PylonChatWidgetContext)
  if (!context) {
    throw new Error(
      'usePylonChatWidget must be used within a PylonChatWidgetProvider'
    )
  }
  return context
}

type EmrCurrentUser = NonNullable<ReturnType<typeof useEmrAuth>['currentUser']>

/**
 * Branch on the feature toggle here so the active implementation never has to
 * check `isEnabled`: when the toggle is off (or no user is logged in) we hand
 * out a no-op context and skip the widget entirely.
 */
export const PylonChatWidgetProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const { currentUser } = useEmrAuth()
  const isEnabled = !!currentUser?.featureToggles.includes('PYLON_CHAT_WIDGET')

  if (!isEnabled || !currentUser) {
    return (
      <PylonChatWidgetContext.Provider value={DISABLED_VALUE}>
        {children}
      </PylonChatWidgetContext.Provider>
    )
  }

  return (
    <EnabledPylonChatWidgetProvider currentUser={currentUser}>
      {children}
    </EnabledPylonChatWidgetProvider>
  )
}

const EnabledPylonChatWidgetProvider = ({
  currentUser,
  children,
}: {
  currentUser: EmrCurrentUser
  children: ReactNode
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  // Tracks the intended open state synchronously. React `isOpen` only updates
  // via the async onShow/onHide callbacks, so a rapid second click would read
  // a stale value; this ref reflects the latest show/hide command immediately.
  const isOpenRef = useRef(false)

  const { data: identityData } = useQuery(PYLON_CHAT_WIDGET_IDENTITY_QUERY, {
    // Identity is per-user; bypass Apollo's cache so logout/login can't seed
    // window.pylon with the previous user's email/email_hash.
    fetchPolicy: 'no-cache',
  })
  const identity = identityData?.pylonChatWidgetIdentity ?? null
  const isReady = !!identity

  // Mount-once lifecycle: inject the loader, suppress Pylon's default bubble,
  // register callbacks. Cleanup runs only on unmount (i.e. the widget being
  // disabled / logout), not when identity refetches, so the panel isn't hidden
  // mid-session.
  useEffect(() => {
    ensurePylonScriptInjected()
    window.Pylon?.('hideChatBubble')
    window.Pylon?.('onShow', () => {
      isOpenRef.current = true
      setIsOpen(true)
    })
    window.Pylon?.('onHide', () => {
      isOpenRef.current = false
      setIsOpen(false)
    })
    window.Pylon?.('onChangeUnreadMessagesCount', (count) =>
      setUnreadCount(count)
    )

    return () => {
      window.Pylon?.('onShow', null)
      window.Pylon?.('onHide', null)
      window.Pylon?.('onChangeUnreadMessagesCount', null)
      window.Pylon?.('hide')
      delete window.pylon
      isOpenRef.current = false
    }
  }, [])

  // Refresh chat_settings whenever identity or display name changes, without
  // tearing down the widget. Overwriting window.pylon is safe — Pylon picks
  // up the next chat_settings on its next interaction.
  useEffect(() => {
    if (!identity) return

    window.pylon = {
      chat_settings: {
        app_id: PYLON_APP_ID,
        email: identity.email,
        name: formatDisplayName(currentUser),
        ...(identity.emailHash ? { email_hash: identity.emailHash } : {}),
      },
    }
  }, [currentUser, identity])

  useEffect(() => {
    const root = document.documentElement

    const updateAnchor = () => {
      const helpBtn = document.querySelector(HELP_NAV_ITEM_SELECTOR)
      if (!helpBtn) return
      const rect = helpBtn.getBoundingClientRect()
      root.style.setProperty(
        '--pylon-anchor-left',
        `${rect.right + PANEL_GAP_PX}px`
      )
      root.style.setProperty(
        '--pylon-anchor-bottom',
        `${window.innerHeight - rect.bottom}px`
      )
    }

    updateAnchor()
    window.addEventListener('resize', updateAnchor)

    // Track sidebar collapse/expand, which moves the Help button horizontally.
    const sidebar = document.querySelector('[data-testid="sidenav"]')
    const observer = sidebar ? new ResizeObserver(updateAnchor) : null
    if (sidebar && observer) observer.observe(sidebar)

    return () => {
      window.removeEventListener('resize', updateAnchor)
      observer?.disconnect()
      root.style.removeProperty('--pylon-anchor-left')
      root.style.removeProperty('--pylon-anchor-bottom')
    }
  }, [])

  const show = useCallback(() => {
    if (!isReady) return
    isOpenRef.current = true
    window.Pylon?.('show')
  }, [isReady])
  const hide = useCallback(() => {
    if (!isReady) return
    isOpenRef.current = false
    window.Pylon?.('hide')
  }, [isReady])
  const toggle = useCallback(() => {
    if (!isReady) return
    // Read the synchronous ref, not React `isOpen`, so back-to-back clicks
    // alternate correctly even before onShow/onHide have fired.
    if (isOpenRef.current) {
      hide()
    } else {
      show()
    }
  }, [isReady, show, hide])

  const value: PylonChatWidgetContextValue = {
    isEnabled: true,
    isReady,
    isOpen,
    unreadCount,
    show,
    hide,
    toggle,
  }

  return (
    <PylonChatWidgetContext.Provider value={value}>
      {children}
    </PylonChatWidgetContext.Provider>
  )
}

export default PylonChatWidgetContext
